// Powerlete "must choose a size" guard. When the variant picker requires an explicit
// selection (no variant pre-selected), block Add to Bag until a size is chosen: scroll the
// picker into view, show an assertive message, highlight the options, and move focus to the
// first available size.
//
// IMPORTANT: the actual add-to-cart fires on the product form's SUBMIT, which Horizon
// dispatches from a single document-level *capture* delegate (see component.js). A
// form-level submit listener therefore runs AFTER Horizon's handler and is too late to stop
// the add. So we intercept the Add to Bag button's CLICK on the stable section root in the
// capture phase instead: cancelling the click prevents the form from submitting at all, so
// nothing is added. The sticky bar puppets the same button via .click(), so this covers it
// too. Listening on the section root (not the form) also survives variant re-renders (morph).
// No external dependencies.
class PwRequireSize extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('.shopify-section') || document;
    this.msg = this.querySelector('.pw-size-required__msg');
    if (!this.root.querySelector('variant-picker')) return;

    this._onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Only guard the product form's Add to Bag button (in-flow or puppeted by the sticky bar).
      const button = target.closest(
        '.buy-buttons-block button[type="submit"], .buy-buttons-block [ref="addToCartButton"]'
      );
      if (!button) return;

      if (this._hasSelection()) {
        this._clear();
        return;
      }
      // No size chosen: cancel the click so the form never submits, then prompt.
      event.preventDefault();
      event.stopImmediatePropagation();
      this._prompt();
    };
    // Capture phase so we run before the click triggers the native form submit.
    this.root.addEventListener('click', this._onClick, { capture: true });

    this._onChange = (event) => {
      if (event.target instanceof Element && event.target.closest('variant-picker')) this._clear();
    };
    this.root.addEventListener('change', this._onChange);
  }

  disconnectedCallback() {
    this.root?.removeEventListener('click', this._onClick, { capture: true });
    this.root?.removeEventListener('change', this._onChange);
  }

  /** The main variant picker, re-queried each time so we survive section re-renders. */
  _picker() {
    return this.root.querySelector('variant-picker');
  }

  /** Selection is satisfied when every option group (fieldset) has a checked radio.
   *  Products with no multi-value option (single/default variant, or a dropdown style)
   *  have no fieldsets, so there is nothing to choose and the guard never blocks. */
  _hasSelection() {
    const picker = this._picker();
    if (!picker) return true;
    const fieldsets = picker.querySelectorAll('fieldset');
    if (fieldsets.length === 0) return true;
    return Array.from(fieldsets).every((fs) => fs.querySelector('input[type="radio"]:checked'));
  }

  _prompt() {
    const picker = this._picker();
    if (!picker) return;
    if (this.msg) this.msg.hidden = false;
    picker.classList.add('pw-size-required-highlight');

    // a11y: tie each unsatisfied option group to the message and mark it invalid, so the
    // requirement is programmatic (not the role="alert"/colour cue alone). Only flag groups
    // with no checked radio, so an already-chosen group on a multi-option product is left alone.
    if (this.msg && this.msg.id) {
      picker.querySelectorAll('fieldset').forEach((fs) => {
        if (fs.querySelector('input[type="radio"]:checked')) return;
        fs.setAttribute('aria-describedby', this.msg.id);
        fs.setAttribute('aria-invalid', 'true');
      });
    }

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    picker.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });

    const firstAvailable = picker.querySelector(
      'input[type="radio"]:not([aria-disabled="true"]):not([disabled])'
    );
    if (firstAvailable) firstAvailable.focus({ preventScroll: true });
  }

  _clear() {
    if (this.msg) this.msg.hidden = true;
    const picker = this._picker();
    picker?.classList.remove('pw-size-required-highlight');
    picker?.querySelectorAll('fieldset').forEach((fs) => {
      fs.removeAttribute('aria-describedby');
      fs.removeAttribute('aria-invalid');
    });
  }
}

if (!customElements.get('pw-require-size')) {
  customElements.define('pw-require-size', PwRequireSize);
}
